import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import StatsRadar from "@/components/StatsRadar";
import XPBar from "@/components/XPBar";
import StreakCalendar from "@/components/StreakCalendar";
import api from "@/lib/api";
import { Flame, Trophy, Target, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const STAT_LABELS = [
  { key: "strength", label: "Strength" },
  { key: "discipline", label: "Discipline" },
  { key: "focus", label: "Focus" },
  { key: "knowledge", label: "Knowledge" },
  { key: "health", label: "Health" },
];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [today, setToday] = useState(null);

  useEffect(() => {
    refresh();
    api.get("/quests/today").then((r) => setToday(r.data)).catch(() => {});
  }, [refresh]);

  if (!user) return null;

  const completedCount = today ? Object.keys(today.completed || {}).length : 0;
  const totalHabits = today ? today.habits.length : 14;
  const questProgress = Math.round((completedCount / totalHabits) * 100);

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tech">System Command</div>
          <h1 className="h-display text-3xl sm:text-4xl mt-1">
            Welcome back, <span className="text-cyan-400">{user.name}</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Today's mission — <span className="text-violet-400 font-mono-tech">{completedCount}/{totalHabits}</span> quests cleared.
          </p>
        </div>
        <Link data-testid="cta-quests" to="/quests" className="btn-neon">
          Open Quest Log
        </Link>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Avatar + XP */}
        <div className="panel panel-corners lg:col-span-5 p-6">
          <div className="label-tech mb-4">Hunter Avatar</div>
          <Avatar stage={user.avatar_stage} level={user.level} name={user.name} />
          <div className="mt-4">
            <XPBar xp={user.xp} xpToNext={user.xp_to_next} level={user.level} />
          </div>
        </div>

        {/* Stats Radar */}
        <div className="panel panel-corners lg:col-span-7 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="label-tech">Attributes</div>
            <div className="text-[0.65rem] text-slate-500 font-mono-tech">MAX 100</div>
          </div>
          <StatsRadar stats={user.stats} />
          <div className="grid grid-cols-5 gap-2 mt-2">
            {STAT_LABELS.map((s) => (
              <div key={s.key} className="text-center border-t border-white/10 pt-2">
                <div className="text-[0.6rem] text-slate-500 font-mono-tech uppercase">{s.label.slice(0, 3)}</div>
                <div className="font-display text-lg text-cyan-300" data-testid={`stat-${s.key}`}>
                  {user.stats?.[s.key] ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="panel p-4 border-l-2 border-l-cyan-400" data-testid="stat-streak">
          <div className="flex items-center justify-between">
            <Flame size={18} className="text-cyan-400" />
            <div className="label-tech">Streak</div>
          </div>
          <div className="h-display text-3xl mt-2">{user.current_streak ?? 0}</div>
          <div className="text-[0.65rem] text-slate-500 font-mono-tech">Days · Best {user.longest_streak ?? 0}</div>
        </div>
        <div className="panel p-4 border-l-2 border-l-violet-400" data-testid="stat-total-quests">
          <div className="flex items-center justify-between">
            <Target size={18} className="text-violet-400" />
            <div className="label-tech">Quests</div>
          </div>
          <div className="h-display text-3xl mt-2">{user.total_quests_completed ?? 0}</div>
          <div className="text-[0.65rem] text-slate-500 font-mono-tech">Total cleared</div>
        </div>
        <div className="panel p-4 border-l-2 border-l-fuchsia-400" data-testid="stat-badges">
          <div className="flex items-center justify-between">
            <Trophy size={18} className="text-fuchsia-400" />
            <div className="label-tech">Badges</div>
          </div>
          <div className="h-display text-3xl mt-2">{user.achievements?.length ?? 0}</div>
          <div className="text-[0.65rem] text-slate-500 font-mono-tech">Unlocked</div>
        </div>
        <div className="panel p-4 border-l-2 border-l-emerald-400" data-testid="stat-today-progress">
          <div className="flex items-center justify-between">
            <Sparkles size={18} className="text-emerald-400" />
            <div className="label-tech">Today</div>
          </div>
          <div className="h-display text-3xl mt-2">{questProgress}%</div>
          <div className="text-[0.65rem] text-slate-500 font-mono-tech">{completedCount}/{totalHabits} quests</div>
        </div>
      </div>

      {/* Streak calendar */}
      <div className="panel panel-corners p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-tech">Ascend Grid</div>
            <div className="h-display text-xl mt-1">Last 90 Days</div>
          </div>
          <div className="text-[0.65rem] text-slate-500 font-mono-tech">
            Consistency &gt; Intensity
          </div>
        </div>
        <StreakCalendar />
      </div>
    </div>
  );
}
